import { useState } from "react";
import { Link } from "react-router-dom";
import { rfqApi } from "../../api";
import { ProcError, ProcLoading, ProcPagination, ProcTabs } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, usePageState } from "../../hooks";
import type { RFQStatus } from "../../types";

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Draft", value: "DRAFT" },
  { label: "Sent", value: "SENT" },
  { label: "Closed", value: "CLOSED" },
];

export default function RFQListPage() {
  const [status, setStatus] = useState<RFQStatus | "">("");
  const { setPage, state } = usePageState();

  const { data, loading, error } = useAsyncData(
    () => rfqApi.list({ status: status || undefined, page: state.page, size: state.size }),
    [status, state.page, state.size],
  );

  function handleTabChange(value: string) {
    setStatus(value as RFQStatus | "");
    setPage(0);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Requests for Quotation</h1>
        </div>
        <Link to="/procurement/rfqs/new" className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
          New RFQ
        </Link>
      </div>

      <ProcTabs tabs={STATUS_TABS} active={status} onChange={handleTabChange} />

      {loading && <ProcLoading />}
      {error && <ProcError message={error} />}

      {!loading && !error && data && (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Serial No</th>
                  <th className="px-4 py-3">Supplier</th>
                  <th className="px-4 py-3">Form Date</th>
                  <th className="px-4 py-3">Deadline</th>
                  <th className="px-4 py-3">Submission</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.content.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No RFQs found.</td></tr>
                )}
                {data.content.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900">{r.serialNo}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {r.toSupplier ?? (r.suppliers?.length
                        ? `${r.suppliers[0].supplierName ?? r.suppliers[0].adHocName ?? "—"}${r.suppliers.length > 1 ? ` +${r.suppliers.length - 1} more` : ""}`
                        : "—")}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {r.date ? new Date(r.date).toLocaleDateString("en-KE") : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {r.deadline ? new Date(r.deadline).toLocaleDateString("en-KE") : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{r.submissionMethod || "—"}</td>
                    <td className="px-4 py-3"><ProcStatusBadge value={r.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/procurement/rfqs/${r.id}`} className="font-semibold text-emerald-700 hover:underline">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ProcPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}
